import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaMicrophone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaEye,
  FaUsers,
  FaCheckCircle,
  FaHourglassHalf,
  FaEdit,
  FaTimes,
  FaSync,
  FaGlobe,
  FaBullseye,
  FaSpinner,
  FaTrash
} from "react-icons/fa";

const ApprovedVoice = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [allReporters, setAllReporters] = useState({});
  const [modifyLoading, setModifyLoading] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedReportersForResend, setSelectedReportersForResend] = useState([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const navigate = useNavigate();

  // Fetch approved Raise Your Voice posts
  const fetchApprovedPosts = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ryv/posts?limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        const apiData = response.data.data; // Get the data property from response.data
        console.log("📊 Raw API data received:", apiData);

        // Check if apiData has the expected structure with posts array
        if (apiData && apiData.posts && Array.isArray(apiData.posts)) {
          console.log(`📊 Total posts received: ${apiData.posts.length}`);

          // Debug: Show all statuses
          const allStatuses = apiData.posts.map(post => post.status);
          console.log("📊 All post statuses:", allStatuses);
          console.log("📊 Unique statuses:", [...new Set(allStatuses)]);

          // Show ALL posts regardless of status - user wants to see all posts
          const allPosts = apiData.posts;

          console.log(`📊 All posts (no status filtering): ${allPosts.length}`);
          console.log("📊 All posts:", allPosts);

          setPosts(allPosts);
          console.log(`📊 State updated with ${allPosts.length} posts`);
        } else if (Array.isArray(apiData)) {
          // Handle case where apiData is directly an array
          console.log(`📊 Direct array received: ${apiData.length} posts`);

          // Debug: Show all statuses
          const allStatuses = apiData.map(post => post.status);
          console.log("📊 All post statuses (direct array):", allStatuses);
          console.log("📊 Unique statuses (direct array):", [...new Set(allStatuses)]);

          // Show ALL posts regardless of status - user wants to see all posts
          const allPosts = apiData;

          console.log(`📊 All posts (no status filtering, direct array): ${allPosts.length}`);
          setPosts(allPosts);
          console.log(`📊 State updated with ${allPosts.length} posts (direct array)`);
        } else {
          console.error("API response.data.data is not in expected format:", apiData);
          console.log("Full response structure:", response.data);
          setPosts([]); // Fallback to empty array to prevent errors
          setError("Received invalid data format from server.");
        }
      } else {
        console.error("API returned success: false:", response.data);
        setPosts([]);
        setError("Failed to fetch posts from server.");
      }
    } catch (error) {
      console.error("Error fetching approved posts:", error);
      setError("Failed to load posts");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchApprovedPosts(true); // Show loading on initial load
  }, []);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Optionally set to end of day if endOfDay=true
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return null;
    }
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const filteredPosts = posts.filter((post) => {
    // Status filtering is already done in fetchApprovedPosts, so we don't need to filter again
    // if (post.status !== "approved" && post.status !== "modified") return false;

    console.log(`🔍 Filtering post ${post.postId || post._id} with status: ${post.status}`);

    // Convert post dates to Date objects
    const postDate = toDate(post.createdAt || post.date);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip posts with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !postDate) {
      return false;
    }

    if (todayOnly) {
      const today = toDate(new Date());
      if (!postDate || postDate.getTime() !== today.getTime()) {
        return false;
      }
    } else if (filterStart && filterEnd) {
      if (!postDate || postDate < filterStart || postDate > filterEnd) {
        return false;
      }
    } else if (filterStart) {
      if (!postDate || postDate < filterStart) {
        return false;
      }
    } else if (filterEnd) {
      if (!postDate || postDate > filterEnd) {
        return false;
      }
    }

    // Search filter
    if (searchText) {
      const s = searchText.toLowerCase();
      const idMatch = (post.postId || post._id) && (post.postId || post._id).toString().toLowerCase().includes(s);
      const titleMatch = post.title && post.title.toLowerCase().includes(s);
      const descriptionMatch = post.description && post.description.toLowerCase().includes(s);
      const stateMatch = post.state && post.state.toLowerCase().includes(s);
      const cityMatch = post.city && post.city.toLowerCase().includes(s);
      const submitterMatch = post.submittedBy?.name && post.submittedBy.name.toLowerCase().includes(s);

      if (!idMatch && !titleMatch && !descriptionMatch && !stateMatch && !cityMatch && !submitterMatch) return false;
    }

    console.log(`✅ Post ${post.postId || post._id} passed all filters`);
    return true;
  });

  console.log(`📊 Total posts in state: ${posts.length}`);
  console.log(`📊 Final filtered posts count: ${filteredPosts.length}`);
  console.log(`📊 Filtered posts:`, filteredPosts.map(p => ({ id: p.postId || p._id, status: p.status })));



  // Fetch all reporters who have the post in their panel (targeted reporters)
  const fetchPostReporters = async (postId, forceRefresh = false) => {
    try {
      console.log(`🔍 Fetching reporters for Raise Your Voice post ${postId}, forceRefresh: ${forceRefresh}`);

      // Add timestamp to prevent caching
      const timestamp = forceRefresh ? `?t=${Date.now()}` : '';

      // Use the correct API endpoint for Raise Your Voice targeted reporters
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/ryv/post/${postId}/targeted-reporters${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Raise Your Voice Post API Response:`, response.data);

      if (response.data.success) {
        console.log(`📊 Fetched reporters for Raise Your Voice post ${postId}:`, response.data.data);
        setAllReporters(prev => ({
          ...prev,
          [postId]: response.data.data
        }));
      } else {
        console.error(`❌ API returned success: false for Raise Your Voice post ${postId}:`, response.data);
      }
    } catch (error) {
      console.error(`❌ Error fetching Raise Your Voice post targeted reporters for ${postId}:`, error);
      if (error.response) {
        console.error(`❌ Error response:`, error.response.data);
        console.error(`❌ Error status:`, error.response.status);
      }
    }
  };

  // View post details
  const handleViewDetails = async (post) => {
    console.log(`🔍 Opening Raise Your Voice post details for: ${post.postId || post._id}`);
    setSelectedPost(post);
    setShowModal(true);

    // Fetch complete post details with reporter information
    try {
      console.log(`🔍 Fetching complete Raise Your Voice post details for: ${post.postId || post._id}`);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/ryv/post/${post.postId || post._id}/targeted-reporters`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Complete Raise Your Voice post details response:`, response.data);

      if (response.data.success) {
        console.log("🔍 Fetched complete Raise Your Voice post details:", response.data.data);

        // Update the selected post with complete details including adminTargeting
        setSelectedPost(prev => ({
          ...prev,
          ...response.data.data,
          adminTargeting: response.data.data.targetingInfo || null
        }));

        // Also update the allReporters state with the fetched data
        setAllReporters(prev => ({
          ...prev,
          [post.postId || post._id]: response.data.data
        }));
      } else {
        console.error("❌ Failed to fetch complete Raise Your Voice post details:", response.data);
      }
    } catch (error) {
      console.error("❌ Error fetching complete Raise Your Voice post details:", error);
      if (error.response) {
        console.error("❌ Error response:", error.response.data);
        console.error("❌ Error status:", error.response.status);
      }
    }
  };

  // Handle modify post
  const handleModifyPost = (post) => {
    navigate("/admin/raise-your-voice/modify", {
      state: {
        postId: post.postId || post._id,
        postData: post
      }
    });
  };

  // Handle resend to all reporters
  const handleResendToReporters = async (post) => {
    setModifyLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ryv/ad/${post.postId || post._id}`,
        {
          action: "modified",
          note: "Resending to all reporters with updated targeting",
          // Keep existing targeting or use default
          allStates: post.adminTargeting?.allStates || false,
          selectedStates: post.adminTargeting?.adminSelectState || [],
          adminSelectCities: post.adminTargeting?.adminSelectCities || [],
          adminSelectPincode: post.adminTargeting?.adminSelectPincode || "",
          reporterId: post.adminTargeting?.reporterId || []
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the posts list
        await fetchApprovedPosts();

        // If modal is open, refresh the reporter data
        if (selectedPost && (selectedPost.postId || selectedPost._id) === (post.postId || post._id)) {
          await fetchPostReporters(post.postId || post._id);
        }

        alert("Post resent to all reporters successfully! Rejected reporters' status has been reset to pending.");
      }
    } catch (error) {
      console.error("Error resending to reporters:", error);
    } finally {
      setModifyLoading(false);
    }
  };

  // Handle resend to rejected reporters only
  const handleResendToRejectedReporters = (post) => {
    const postReporters = allReporters[post.postId || post._id];
    if (postReporters && postReporters.grouped && postReporters.grouped.rejected.length > 0) {
      setSelectedPost(post);
      setShowResendModal(true);
      // Pre-select all rejected reporters
      setSelectedReportersForResend(postReporters.grouped.rejected.map(r => r.reporterId));
    }
  };

  // Handle resend to selected rejected reporters
  const handleResendToSelectedReporters = async () => {
    if (selectedReportersForResend.length === 0) {
      alert("Please select at least one reporter to resend");
      return;
    }

    setResendLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ryv/ad/${selectedPost.postId || selectedPost._id}`,
        {
          action: "modified",
          note: `Resending to ${selectedReportersForResend.length} selected rejected reporters`,
          // Use specific reporter targeting for resend
          allStates: false,
          selectedStates: [],
          adminSelectCities: [],
          adminSelectPincode: "",
          reporterId: selectedReportersForResend
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setShowResendModal(false);
        setSelectedReportersForResend([]);

        // Refresh the reporter data for this specific post (force refresh)
        await fetchPostReporters(selectedPost.postId || selectedPost._id, true);

        // Also refresh the main posts list
        await fetchApprovedPosts();

        alert(`Post resent to ${selectedReportersForResend.length} reporters successfully! Their status has been reset to pending.`);
      }
    } catch (error) {
      console.error("Error resending to selected reporters:", error);
    } finally {
      setResendLoading(false);
    }
  };

  // Handle delete reporter from post
  const handleDeleteReporter = async (postId, reporterId, reporterName) => {
    if (!window.confirm(`Are you sure you want to remove ${reporterName} from this post? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [reporterId]: true }));

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/delete/ryv/post/reporter/${postId}/${reporterId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the reporter list for this post (force refresh)
        await fetchPostReporters(postId, true);
        alert(`${reporterName} has been removed from the post successfully.`);
      }
    } catch (error) {
      console.error("Error deleting reporter from post:", error);

      // Show specific error message from backend if available
      if (error.response && error.response.data && error.response.data.message) {
        alert(`Failed to remove reporter: ${error.response.data.message}`);
      } else {
        alert("Failed to remove reporter from post. Please try again.");
      }
    } finally {
      setDeleteLoading(prev => ({ ...prev, [reporterId]: false }));
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <>
      {/* Compact Search/Filter Bar */}
      <div className="w-full sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md border-b mb-4 py-3 px-4">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-3 items-center">
            {/* Compact Date Range Filter */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2">
              <FaCalendarAlt className="text-blue-600 text-sm" />
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setTodayOnly(false);
                }}
                max={endDate || undefined}
                placeholder="Start date"
              />
              <span className="text-gray-400 text-sm">→</span>
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setTodayOnly(false);
                }}
                min={startDate || undefined}
                placeholder="End date"
              />
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate(today);
                  setTodayOnly(true);
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${todayOnly
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Today
              </button>
            </div>

            {/* Compact Search Input */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2 flex-1 max-w-md">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by Conference ID, State, or City..."
                className="flex-1 border-0 bg-transparent text-sm focus:ring-0 focus:outline-none"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Quick Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSearchText("");
                  setStartDate("");
                  setEndDate("");
                  setTodayOnly(false);
                }}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          All Raise Your Voice Posts
        </h2>
        <div className="flex flex-wrap gap-2 sm:space-x-2 sm:space-y-0">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/pendingvoice")}
          >
            Pending
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/modifiedvoice")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/rejectedvoice")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/runningvoice")}
          >
            Running
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/completedvoice")}
          >
            Completed
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Post ID</th>
              <th className="px-4 py-2 text-left">Date & Time</th>
              <th className="px-4 py-2 text-left">Location</th>
              <th className="px-4 py-2 text-left">Targeting</th>
              <th className="px-4 py-2 text-left">Reporter Status</th>
              <th className="px-4 py-2 text-left">Proof Status</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPosts.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No posts found.
                </td>
              </tr>
            ) : (
              (() => {
                const totalPages = Math.ceil(filteredPosts.length / rowsPerPage);
                const paginatedPosts = filteredPosts.slice(
                  (currentPage - 1) * rowsPerPage,
                  currentPage * rowsPerPage
                );
                return paginatedPosts.map((post) => (
                  <tr key={post._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-sm">{post.postId || post._id}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="mr-1 text-blue-500" />
                        {new Date(post.createdAt || post.date).toLocaleDateString('en-IN')}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <FaClock className="mr-1" />
                        {new Date(post.createdAt || post.date).toLocaleTimeString('en-IN')}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaMapMarkerAlt className="mr-1 text-green-500" />
                        {post.city}, {post.state}
                      </div>
                      <div className="text-xs text-gray-500">{post.residenceAddress || post.location || "N/A"}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        {post.adminTargeting ? (
                          <>
                            {post.adminTargeting.allStates ? (
                              <><FaGlobe className="mr-1 text-purple-500" />All States</>
                            ) : post.adminTargeting.adminSelectState?.length > 0 ? (
                              <><FaBullseye className="mr-1 text-purple-500" />{post.adminTargeting.adminSelectState.length} States</>
                            ) : post.adminTargeting.adminSelectCities?.length > 0 ? (
                              <><FaBullseye className="mr-1 text-purple-500" />{post.adminTargeting.adminSelectCities.length} Cities</>
                            ) : post.adminTargeting.reporterId?.length > 0 ? (
                              <><FaUsers className="mr-1 text-purple-500" />{post.adminTargeting.reporterId.length} Reporters</>
                            ) : (
                              <><FaMapMarkerAlt className="mr-1 text-gray-500" />Default</>
                            )}
                          </>
                        ) : (
                          <><FaMapMarkerAlt className="mr-1 text-gray-500" />Default</>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {post.status === "modified" ? "Modified targeting" : "Original location"}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaUsers className="mr-1 text-blue-500" />
                        {post.totalAcceptedReporters || 0} accepted
                      </div>
                      <div className="text-xs text-gray-500">
                        {allReporters[post.postId || post._id]?.rejected || 0} rejected, {allReporters[post.postId || post._id]?.pending || 0} pending
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaCheckCircle className="mr-1 text-green-500" />
                        {post.totalProofSubmitted || 0} submitted
                      </div>
                      <div className="text-xs text-gray-500">
                        {post.totalProofPending || 0} pending
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${post.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : post.status === "modified"
                          ? "bg-orange-100 text-orange-800"
                          : post.status === "running"
                            ? "bg-blue-100 text-blue-800"
                            : post.status === "completed"
                              ? "bg-purple-100 text-purple-800"
                              : post.status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : post.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                        }`}>
                        {post.status === "approved" ? "Approved" :
                          post.status === "modified" ? "Modified" :
                            post.status === "running" ? "Running" :
                              post.status === "completed" ? "Completed" :
                                post.status === "pending" ? "Pending" :
                                  post.status === "rejected" ? "Rejected" :
                                    post.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <button
                          className="px-2 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-xs"
                          onClick={() => handleViewDetails(post)}
                        >
                          <FaEye className="mr-1" />
                          View
                        </button>
                        <button
                          className="px-2 py-1 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center text-xs"
                          onClick={() => handleModifyPost(post)}
                        >
                          <FaEdit className="mr-1" />
                          Modify
                        </button>
                        {allReporters[post.postId || post._id]?.rejected > 0 && (
                          <button
                            className="px-2 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-xs"
                            onClick={() => handleResendToRejectedReporters(post)}
                          >
                            <FaSync className="mr-1" />
                            Resend to Reporters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
              })()
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {(() => {
        const totalPages = Math.ceil(filteredPosts.length / rowsPerPage);
        return totalPages > 1 ? (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>
              <span className="px-4 py-1 rounded border border-green-400 bg-green-300 text-green-900 font-bold">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Next
              </button>
            </nav>
          </div>
        ) : null;
      })()}

      {/* Post Details Modal */}
      {showModal && selectedPost && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-green-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaMicrophone className="mr-2 text-blue-500" />
                {selectedPost.name || selectedPost.title || selectedPost.topic || "Raise Your Voice Post"}
              </h2>
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${selectedPost.status === "approved"
                ? "bg-green-100 text-green-800"
                : selectedPost.status === "modified"
                  ? "bg-orange-100 text-orange-800"
                  : selectedPost.status === "running"
                    ? "bg-blue-100 text-blue-800"
                    : selectedPost.status === "completed"
                      ? "bg-purple-100 text-purple-800"
                      : selectedPost.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : selectedPost.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                }`}>
                {selectedPost.status === "approved" ? "Approved" :
                  selectedPost.status === "modified" ? "Modified" :
                    selectedPost.status === "running" ? "Running" :
                      selectedPost.status === "completed" ? "Completed" :
                        selectedPost.status === "pending" ? "Pending" :
                          selectedPost.status === "rejected" ? "Rejected" :
                            selectedPost.status}
              </span>
            </div>

            {/* Post Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Post Information</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Post ID:</strong> <span className="font-mono">{selectedPost.postId || selectedPost._id}</span></div>
                  <div><strong>Date:</strong> {new Date(selectedPost.createdAt || selectedPost.date).toLocaleDateString('en-IN')}</div>
                  <div><strong>Time:</strong> {new Date(selectedPost.createdAt || selectedPost.date).toLocaleTimeString('en-IN')}</div>
                  <div><strong>Submitter:</strong> {selectedPost.name || "N/A"}</div>
                  <div><strong>Email:</strong> {selectedPost.email || "N/A"}</div>
                  <div><strong>Phone:</strong> {selectedPost.phoneNo || "N/A"}</div>
                  <div><strong>Description:</strong> {selectedPost.description || "N/A"}</div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">Location Details</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>State:</strong> {selectedPost.state || "N/A"}</div>
                  <div><strong>City:</strong> {selectedPost.city || "N/A"}</div>
                  <div><strong>Address:</strong> {selectedPost.residenceAddress || selectedPost.location || "N/A"}</div>
                  <div><strong>Landmark:</strong> {selectedPost.landmark || "N/A"}</div>
                </div>
              </div>
            </div>

            {/* All Users Who Have Post in Their Panel */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                All Users Who Have This Post in Their Panel
                {selectedPost.targetUserType && (
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    (Target: {selectedPost.targetUserType === "both" ? "Both Reporters & Influencers" :
                      selectedPost.targetUserType === "reporter" ? "Reporters Only" :
                        selectedPost.targetUserType === "influencer" ? "Influencers Only" : "Unknown"})
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                These are all the users who received notifications and have this post available in their panel to accept or reject.
              </p>

              {allReporters[selectedPost.postId || selectedPost._id] ? (
                <>

                  {/* User Statistics - Compact with Highlights */}
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
                    <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-gray-700">{allReporters[selectedPost.postId || selectedPost._id].totalTargetedUsers || allReporters[selectedPost.postId || selectedPost._id].totalTargetedReporters}</div>
                      <div className="text-xs text-gray-600 font-medium">Total in Panel</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-purple-50 to-purple-100 p-2 rounded-lg border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-purple-700">{allReporters[selectedPost.postId || selectedPost._id].totalTargetedReporters || 0}</div>
                      <div className="text-xs text-purple-600 font-medium">Reporters</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-pink-50 to-pink-100 p-2 rounded-lg border border-pink-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-pink-700">{allReporters[selectedPost.postId || selectedPost._id].totalTargetedInfluencers || 0}</div>
                      <div className="text-xs text-pink-600 font-medium">Influencers</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-yellow-700">{allReporters[selectedPost.postId || selectedPost._id].pending}</div>
                      <div className="text-xs text-yellow-600 font-medium">Pending</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-green-700">{allReporters[selectedPost.postId || selectedPost._id].accepted}</div>
                      <div className="text-xs text-green-600 font-medium">Accepted</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-red-700">{allReporters[selectedPost.postId || selectedPost._id].rejected}</div>
                      <div className="text-xs text-red-600 font-medium">Rejected</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-blue-700">{allReporters[selectedPost.postId || selectedPost._id].completed || 0}</div>
                      <div className="text-xs text-blue-600 font-medium">Completed</div>
                    </div>
                  </div>

                  {/* All Users Table - Compact with Highlights */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-sm border border-gray-200">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">IINSAF ID</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User Type</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Notes</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(allReporters[selectedPost.postId || selectedPost._id].users || allReporters[selectedPost.postId || selectedPost._id].reporters || []).map((user, index) => (
                          <tr key={index} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${user.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                            user.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                              user.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                                "bg-white"
                            }`}>
                            <td className="px-2 py-2 text-xs font-mono font-semibold text-gray-800">{user.iinsafId || "N/A"}</td>
                            <td className="px-2 py-2 text-xs font-semibold text-gray-800">{user.userName || user.reporterName || "N/A"}</td>
                            <td className="px-2 py-2 text-xs text-gray-600 truncate max-w-32">{user.userEmail || user.reporterEmail || "N/A"}</td>
                            <td className="px-2 py-2 text-xs">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.userType === "reporter"
                                ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                                : "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-pink-200"
                                }`}>
                                {user.userType === "reporter" ? "Reporter" : "Influencer"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-600">{user.userCity || user.reporterCity}, {user.userState || user.reporterState}</td>
                            <td className="px-2 py-2 text-xs">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.status === "accepted"
                                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                                : user.status === "rejected"
                                  ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                  : user.status === "completed"
                                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                    : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                                }`}>
                                {user.status === "accepted" ? "Accepted" :
                                  user.status === "rejected" ? "Rejected" :
                                    user.status === "completed" ? "Completed" : "Pending"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-600">
                              {user.status === "completed" && user.completedAt ?
                                new Date(user.completedAt).toLocaleDateString('en-IN') :
                                user.acceptedAt ? new Date(user.acceptedAt).toLocaleDateString('en-IN') :
                                  user.rejectedAt ? new Date(user.rejectedAt).toLocaleDateString('en-IN') :
                                    "No response"}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {user.status === "completed" ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">Proof approved</span>
                              ) : user.rejectNote ? (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-semibold">Rejected</span>
                              ) : user.proofSubmitted ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">Proof submitted</span>
                              ) : (
                                <span className="text-gray-400">No notes</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {user.status === "pending" ? (
                                <span className="text-gray-400 text-xs font-medium">No action</span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteReporter(selectedPost.postId || selectedPost._id, user.userId || user.reporterId, user.userName || user.reporterName)}
                                  disabled={deleteLoading[user.userId || user.reporterId]}
                                  className={`px-2 py-1 rounded-md text-xs font-bold transition-all duration-200 flex items-center shadow-sm ${deleteLoading[user.userId || user.reporterId]
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200 hover:shadow-md'
                                    }`}
                                  title="Remove user from post (only available for users who have responded)"
                                >
                                  {deleteLoading[user.userId || user.reporterId] ? (
                                    <>
                                      <FaSpinner className="animate-spin mr-1" />
                                      Removing...
                                    </>
                                  ) : (
                                    <>
                                      <FaTrash className="mr-1" />
                                      Remove
                                    </>
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    <FaUsers className="mx-auto text-4xl mb-2" />
                    <p>No reporter data available</p>
                    <p className="text-sm">This Raise Your Voice post may not have any targeted reporters yet.</p>
                  </div>
                  <button
                    onClick={() => fetchPostReporters(selectedPost.postId || selectedPost._id, true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center mx-auto"
                  >
                    <FaSync className="mr-2" />
                    Refresh Reporter Data
                  </button>
                </div>
              )}
            </div>

            {/* Admin Targeting Information */}
            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">Admin Targeting Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {selectedPost.adminTargeting ? (
                  <>
                    <div><strong>Target User Type:</strong>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-bold ${selectedPost.adminTargeting.targetUserType === "both" ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" :
                        selectedPost.adminTargeting.targetUserType === "reporter" ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white" :
                          selectedPost.adminTargeting.targetUserType === "influencer" ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white" :
                            "bg-gray-500 text-white"
                        }`}>
                        {selectedPost.adminTargeting.targetUserType === "both" ? "Both Reporters & Influencers" :
                          selectedPost.adminTargeting.targetUserType === "reporter" ? "Reporters Only" :
                            selectedPost.adminTargeting.targetUserType === "influencer" ? "Influencers Only" :
                              selectedPost.adminTargeting.targetUserType || "Unknown"}
                      </span>
                    </div>
                    <div><strong>All States:</strong> {selectedPost.adminTargeting.allStates ? "Yes" : "No"}</div>
                    <div><strong>Selected States:</strong> {selectedPost.adminTargeting.adminSelectState?.join(", ") || "None"}</div>
                    <div><strong>Selected Cities:</strong> {selectedPost.adminTargeting.adminSelectCities?.join(", ") || "None"}</div>
                    <div><strong>Selected Pincode:</strong> {selectedPost.adminTargeting.adminSelectPincode || "None"}</div>
                    <div><strong>Post Reporters:</strong> {selectedPost.numberOfReporters || 0} need to complete the post</div>
                    <div><strong>Total Targeted Users:</strong> {allReporters[selectedPost.postId || selectedPost._id]?.totalTargetedUsers || allReporters[selectedPost.postId || selectedPost._id]?.totalTargetedReporters || 0} users</div>
                    <div><strong>Total Reporters:</strong> {allReporters[selectedPost.postId || selectedPost._id]?.totalTargetedReporters || 0} reporters</div>
                    <div><strong>Total Influencers:</strong> {allReporters[selectedPost.postId || selectedPost._id]?.totalTargetedInfluencers || 0} influencers</div>
                    <div><strong>Targeting Method:</strong> {selectedPost.adminTargeting.reporterId?.length > 0 ? "Specific User Selection" :
                      selectedPost.adminTargeting.allStates ? "All States" :
                        selectedPost.adminTargeting.adminSelectState?.length > 0 ? "Selected States/Cities" :
                          "Location-based (State/City)"}</div>
                    <div><strong>Modified At:</strong> {(() => {
                      console.log("🔍 Debug - selectedPost.adminTargeting:", selectedPost.adminTargeting);
                      console.log("🔍 Debug - modifiedAt value:", selectedPost.adminTargeting.modifiedAt);
                      console.log("🔍 Debug - modifiedAt type:", typeof selectedPost.adminTargeting.modifiedAt);
                      return selectedPost.adminTargeting.modifiedAt ? new Date(selectedPost.adminTargeting.modifiedAt).toLocaleString('en-IN') : "N/A";
                    })()}</div>
                  </>
                ) : (
                  <>
                    <div><strong>Target User Type:</strong>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-bold ${selectedPost.targetUserType === "both" ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" :
                        selectedPost.targetUserType === "reporter" ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white" :
                          selectedPost.targetUserType === "influencer" ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white" :
                            "bg-gray-500 text-white"
                        }`}>
                        {selectedPost.targetUserType === "both" ? "Both Reporters & Influencers" :
                          selectedPost.targetUserType === "reporter" ? "Reporters Only" :
                            selectedPost.targetUserType === "influencer" ? "Influencers Only" :
                              selectedPost.targetUserType || "Unknown"}
                      </span>
                    </div>
                    <div><strong>All States:</strong> No</div>
                    <div><strong>Selected States:</strong> {selectedPost.state || "None"}</div>
                    <div><strong>Selected Cities:</strong> {selectedPost.city || "None"}</div>
                    <div><strong>Selected Pincode:</strong> None</div>
                    <div><strong>Requested Reporters:</strong> {selectedPost.numberOfReporters || 0} reporters</div>
                    <div><strong>Total Targeted Reporters:</strong> {allReporters[selectedPost.postId || selectedPost._id]?.totalTargetedReporters || 0} reporters</div>
                    <div><strong>Targeting Method:</strong> {selectedPost.reporterId?.length > 0 ? "Specific Reporter Selection" :
                      selectedPost.allStates ? "All States" :
                        selectedPost.adminSelectState?.length > 0 ? "Selected States/Cities" :
                          "Location-based (State/City)"}</div>
                    <div><strong>Modified At:</strong> {selectedPost.modifiedAt ? new Date(selectedPost.modifiedAt).toLocaleString('en-IN') : "N/A"}</div>
                  </>
                )}
              </div>
            </div>


            {/* Admin Actions */}
            <div className="bg-yellow-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
                  onClick={() => handleModifyPost(selectedPost)}
                >
                  <FaEdit className="mr-2" />
                  Modify Targeting
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                  onClick={() => setShowModal(false)}
                >
                  <FaEye className="mr-2" />
                  Close Details
                </button>
              </div>
            </div>

            {/* Submitter Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Submitted By</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Name:</strong> {selectedPost.submittedBy?.name || "N/A"}</div>
                <div><strong>Organization:</strong> {selectedPost.submittedBy?.organization || "N/A"}</div>
                <div><strong>Email:</strong> {selectedPost.submittedBy?.email || "N/A"}</div>
                <div><strong>User ID:</strong> {selectedPost.submittedBy?.userId || selectedPost.submittedBy?._id || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend to Reporters Modal */}
      {showResendModal && selectedPost && allReporters[selectedPost.postId || selectedPost._id] && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-blue-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowResendModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaSync className="mr-2 text-blue-500" />
                Resend Post to Reporters
              </h2>
              <p className="text-gray-600">Select which reporters to resend the post to</p>
            </div>

            {/* Post Info */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Post: {selectedPost.title || selectedPost.topic || "Raise Your Voice Post"}</h3>
              <p className="text-sm text-blue-700">Post ID: {selectedPost.postId || selectedPost._id}</p>
            </div>

            {/* Reporter Status Summary - Compact with Highlights */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-lg font-bold text-gray-700">{allReporters[selectedPost.postId || selectedPost._id].totalTargetedReporters}</div>
                <div className="text-xs text-gray-600 font-medium">Total</div>
              </div>
              <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm">
                <div className="text-lg font-bold text-yellow-700">{allReporters[selectedPost.postId || selectedPost._id].pending}</div>
                <div className="text-xs text-yellow-600 font-medium">Pending</div>
              </div>
              <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm">
                <div className="text-lg font-bold text-green-700">{allReporters[selectedPost.postId || selectedPost._id].accepted}</div>
                <div className="text-xs text-green-600 font-medium">Accepted</div>
              </div>
              <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm">
                <div className="text-lg font-bold text-red-700">{allReporters[selectedPost.postId || selectedPost._id].rejected}</div>
                <div className="text-xs text-red-600 font-medium">Rejected</div>
              </div>
              <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm">
                <div className="text-lg font-bold text-blue-700">{allReporters[selectedPost.postId || selectedPost._id].completed || 0}</div>
                <div className="text-xs text-blue-600 font-medium">Completed</div>
              </div>
            </div>

            {/* All Users List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                All Users Who Have Post in Panel ({allReporters[selectedPost.postId || selectedPost._id].totalTargetedUsers || allReporters[selectedPost.postId || selectedPost._id].totalTargetedReporters})
              </h3>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {(allReporters[selectedPost.postId || selectedPost._id].users || allReporters[selectedPost.postId || selectedPost._id].reporters || []).map((user, index) => (
                  <div key={index} className={`flex items-center p-2 border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${user.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                    user.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                      user.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                        "bg-white"
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedReportersForResend.includes(user.userId || user.reporterId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReportersForResend(prev => [...prev, user.userId || user.reporterId]);
                        } else {
                          setSelectedReportersForResend(prev => prev.filter(id => id !== (user.userId || user.reporterId)));
                        }
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{user.userName || user.reporterName}</div>
                          <div className="text-xs text-gray-500 truncate max-w-48">{user.userEmail || user.reporterEmail}</div>
                          <div className="text-xs text-gray-500">{user.userCity || user.reporterCity}, {user.userState || user.reporterState}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono text-gray-600 font-semibold">{user.iinsafId}</div>
                          <div className="text-xs mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.userType === "reporter"
                              ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200"
                              : "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-pink-200"
                              }`}>
                              {user.userType === "reporter" ? "Reporter" : "Influencer"}
                            </span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.status === "accepted"
                              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                              : user.status === "rejected"
                                ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                : user.status === "completed"
                                  ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                  : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                              }`}>
                              {user.status === "accepted" ? "Accepted" :
                                user.status === "rejected" ? "Rejected" :
                                  user.status === "completed" ? "Completed" : "Pending"}
                            </span>
                          </div>
                          {user.status === "rejected" && user.rejectedAt && (
                            <div className="text-xs text-red-600 mt-1">
                              {new Date(user.rejectedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {user.status === "accepted" && user.acceptedAt && (
                            <div className="text-xs text-green-600 mt-1">
                              {new Date(user.acceptedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {user.status === "completed" && user.completedAt && (
                            <div className="text-xs text-blue-600 mt-1">
                              {new Date(user.completedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                        </div>
                      </div>
                      {user.rejectNote && (
                        <div className="mt-1 p-1 bg-red-100 rounded text-xs text-red-800">
                          <strong>Reason:</strong> {user.rejectNote}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selection Summary */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-800">Selected Reporters</h4>
                  <p className="text-sm text-blue-600">
                    {selectedReportersForResend.length} of {allReporters[selectedPost.postId || selectedPost._id].totalTargetedReporters} reporters selected
                  </p>
                  <div className="text-xs text-gray-600 mt-1">
                    Selected: {selectedReportersForResend.length} |
                    Pending: {allReporters[selectedPost.postId || selectedPost._id].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "pending").length} |
                    Accepted: {allReporters[selectedPost.postId || selectedPost._id].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "accepted").length} |
                    Rejected: {allReporters[selectedPost.postId || selectedPost._id].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "rejected").length} |
                    Completed: {allReporters[selectedPost.postId || selectedPost._id].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "completed").length}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedReportersForResend(
                      allReporters[selectedPost.postId || selectedPost._id].reporters.map(r => r.reporterId)
                    )}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend(
                      allReporters[selectedPost.postId || selectedPost._id].grouped.rejected.map(r => r.reporterId)
                    )}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    Select Rejected Only
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend([])}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResendModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleResendToSelectedReporters}
                disabled={selectedReportersForResend.length === 0 || resendLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {resendLoading ? (
                  <>
                    <FaSpinner className="mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <FaSync className="mr-2" />
                    Resend to {selectedReportersForResend.length} Reporters
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovedVoice;
